/**
 * agreement-key-terms.ts — the plain-English half of the ceremony.
 *
 * A driver signing a twelve-page hire purchase agreement on a phone reads the whole
 * document, but what they actually have to CHECK is four figures: which vehicle, at what
 * price, for what weekly instalment, against what deposit. Those four are stated in plain
 * English before the reader opens, and then confirmed one tick at a time afterwards — the
 * same list, twice, so the confirmation is a confirmation of something that was said and
 * not a new set of statements to skim.
 *
 * ── WHY NOT `agreement_render`'s MERGE VALUES ─────────────────────────────────
 * They are admin-only: `agreement_render` returns `values: null` to a driver, on purpose.
 * The figures therefore come from `get_contract_insights`, which is the driver's own
 * contract payload and is already in the query cache by the time a ceremony opens.
 *
 * ── WHICH SUBJECT ─────────────────────────────────────────────────────────────
 * A contract can carry several loans and several insurance renewals, so "the loan
 * agreement" is not a thing on its own. Every document names its subject through a typed
 * arc column (`loan_agreement_id`, `insurance_loan_agreement_id`,
 * `contract_deposit_agreement_id`), and those ids are the ids of the legacy agreement rows
 * the insights payload already lists — so the subject is resolved by matching them, never
 * by taking the first row of the right kind.
 *
 * A term is emitted only when its figure resolves. A document whose figures cannot be
 * read still gets one term — read the document, confirm it is correct — because a
 * checklist with nothing on it is a step that teaches a signer to tap through.
 *
 * ── WHY THE TERM AND THE EXPECTED COMPLETION ARE NOT HERE ─────────────────────
 * The hire purchase Schedule now prints both, and neither belongs on this list. Clause 2.5
 * of that Schedule says in terms that they are ESTIMATES, that they assume every instalment
 * is paid as it falls due, and that neither fixes a date on which anything falls due — so a
 * tick-box reading "I confirm I finish on 14 February 2028" would collect a confirmation of
 * the one thing the document declines to promise. They are also derived, not stored: the
 * resolver behind those two merge keys reproduces the engine's own instalment arithmetic
 * (floor, dust guard, final-by-subtraction, anchored at the first instalment), and a second
 * implementation of that here would be a money figure computed twice.
 */

import { format } from 'date-fns';

import type { ContractInsightsData } from '@/pages/ContractDetail';

import { latestPremiumPaymentAt } from './insurance-premium-shape';

export interface KeyTerm {
    id: string;
    /** Column heading in the confirmation list: "The vehicle", "My instalment". */
    title: string;
    /** The plain-English statement shown before the document opens. */
    summary: string;
    /**
     * The supporting line under it. OPTIONAL, because a supporting line that cannot be stated
     * truthfully is better absent than approximated: the row's own statement is the term, and
     * the line under it is a fact about that term which the payload may not carry. `KeyTermRow`
     * omits the line where there is none, the way `ConfirmRow` beside it already does.
     */
    detail?: string;
    /** First person, for the tick-to-confirm step. */
    confirmation: string;
}

/** The arc columns a document carries, as the driver app reads them. */
export interface AgreementSubjectRefs {
    doc_type: string;
    reference_no?: string | null;
    /**
     * THE RENDERED DOCUMENT'S OWN TITLE, from `agreement_render` — the pinned template's, not
     * this app's guess at one. One `doc_type` now carries two instruments (`INSURANCE_LOAN` is
     * both the Advance and the Payment Record [D-B45]) and the title is the only thing on the
     * payload that separates them. Optional: a caller that has not rendered yet gets the
     * advance checklist, which is the one that has always been true here.
     */
    title?: string | null;
    loan_agreement_id?: number | null;
    insurance_loan_agreement_id?: number | null;
    contract_deposit_agreement_id?: number | null;
}

const currency = new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
});

function money(amount: number | null | undefined): string | null {
    if (amount === null || amount === undefined || !Number.isFinite(amount)) return null;
    return currency.format(amount);
}

function day(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : format(date, 'MMM d, yyyy');
}

/**
 * A CALENDAR DAY, READ AS A CALENDAR DAY — the formatter for `date` columns, which `day()`
 * above is not.
 *
 * `day()` takes an INSTANT: `new Date('2026-03-02')` is midnight UTC, and `format` then renders
 * it in the VIEWER's zone, so a driver reading from anywhere behind UTC is shown "Mar 1, 2026"
 * for a Monday the database calls the 2nd. On a timestamp that is a rounding of an instant to a
 * day; on `loan_instalments.starts_at`, which is a `date` and has no instant in it at all, it is
 * simply the wrong day — and the day this checklist prints is the Monday the driver ticks a box
 * to say their repayments start on.
 *
 * So the string is split on its own calendar parts and rebuilt at LOCAL midnight, which is what
 * `businessDay()` produces in fleet-admin and what every date-fns pattern in this app expects.
 * The business timezone is Africa/Accra — UTC+0 year-round — so a full timestamp read for its
 * first ten characters gives the business day too; anything that is not `YYYY-MM-DD`-shaped is
 * no date at all rather than a guess.
 */
const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

function calendarDay(value: string | null | undefined): string | null {
    if (!value) return null;
    const parts = CALENDAR_DAY.exec(value);
    if (!parts) return null;
    const date = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
    return Number.isNaN(date.getTime()) ? null : format(date, 'MMM d, yyyy');
}

function percent(rate: number | null | undefined): string | null {
    if (rate === null || rate === undefined || !Number.isFinite(rate)) return null;
    return `${rate}%`;
}

/**
 * A SERVED STRING THAT IS ACTUALLY A STRING — blank is absent, the way the server reads it.
 *
 * `??` falls back only on null and undefined, so a column holding spaces wins the chain and
 * prints an empty supporting line. `agreement_merge_data` asks the same question of the same
 * columns with `NULLIF(btrim(…), '')`, which is why a whitespace `loans.description` is a
 * render-blocking fill-in on the Schedule rather than a value — and a checklist that quotes the
 * Schedule cannot treat as present what the Schedule refuses to print.
 */
function text(value: string | null | undefined): string | null {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    return trimmed.length > 0 ? trimmed : null;
}

/**
 * The divisor the overdue-interest engine uses, and the one the resolver divides by. That
 * engine is `public.calculate_overdue_interest` in the database — the single source of truth
 * for overdue interest since the client-side ports were retired.
 */
const DAYS_PER_YEAR = 365;

/**
 * The overdue rate AS THE SCHEDULE PRINTS IT: per day, to four decimal places, trailing
 * zeros trimmed.
 *
 * `contracts.overdue_interest_rate` is stored per annum and charged per day, and the hire
 * purchase Schedule makes the DAILY figure the operative one — "Interest on a late payment:
 * {{contract.overdue_daily_rate}} per cent per day, charged on the amount unpaid". So this
 * checklist has to state the same number the document states, arrived at the same way:
 * `agreement_merge_data` resolves that key as `to_char(rate / 365, 'FM990.0000')` with the
 * trailing zeros and any bare point trimmed off.
 *
 * FOUR PLACES AND NOT TWO IS THE WHOLE POINT. A 400 per cent year is 1.0959 a day; printed
 * at two it reads 1.1, which is a different arrears figure from the one the engine charges —
 * and this is the list a driver ticks to say they understood the charge. A new contract is
 * born at 365, which is 1 a day exactly and needs no places at all; the contracts that need
 * the precision are exactly the ones this formatter exists for.
 */
function dailyPercent(annualRate: number | null | undefined): string | null {
    if (annualRate === null || annualRate === undefined || !Number.isFinite(annualRate)) {
        return null;
    }
    const daily = (annualRate / DAYS_PER_YEAR).toFixed(4);
    return daily.replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * The Schedule's own Weekly Instalment, picked the way the Schedule's resolver picks it.
 *
 * ── IT IS NOT `weekly_rate`, AND THE DIFFERENCE IS MONEY ──────────────────────
 * `{{contract.weekly_instalment}}` resolves to `contract_instalments.amount` ALONE, latest
 * row by `starts_at DESC` — fleet-backend `20260816010000`:309-313:
 *
 *     SELECT ci.amount INTO v_weekly
 *       FROM contract_instalments ci
 *      WHERE ci.contract_id = v_contract_id
 *      ORDER BY ci.starts_at DESC
 *      LIMIT 1;
 *
 * `get_contract_insights`' `weekly_rate` answers a DIFFERENT question: it is the hire
 * purchase instalment PLUS the current instalment of every advance and insurance loan on the
 * contract (`20260728020000`:1444-1465), which is what the driver actually pays out in a
 * week. Both figures are right for their own surface — `weekly_rate` is the honest number on
 * a dashboard — but a screen that QUOTES THE AGREEMENT has to state the agreement's number,
 * and on any contract carrying an advance the two disagree (500 against 550 on live data).
 *
 * ── NULL, NEVER ZERO ──────────────────────────────────────────────────────────
 * A contract with no instalment row has no Weekly Instalment to state, and both callers omit
 * an absent figure. A zero would print "GHS 0.00 every Monday", which is a term no Schedule
 * carries.
 *
 * The payload already orders this array `starts_at DESC`, so the pick is normally its first
 * element — but the maximum is taken explicitly rather than trusting a server-side ORDER BY
 * to survive a future edit to the query. `starts_at` is a `date`, so its ISO strings compare
 * lexicographically in calendar order and no instant is parsed out of a calendar day.
 */
export function scheduleWeeklyInstalment(
    data: ContractInsightsData | null | undefined,
): number | null {
    let latest: { amount: number; starts_at: string } | null = null;
    for (const row of data?.instalments ?? []) {
        if (!row || typeof row.starts_at !== 'string' || !Number.isFinite(row.amount)) continue;
        if (latest === null || row.starts_at > latest.starts_at) latest = row;
    }
    return latest === null ? null : latest.amount;
}

/**
 * The Schedule's own "Deposit credited", picked the way the Schedule's resolver picks it.
 *
 * ── THE COUNT IS THE GUARD, NOT THE AMOUNT ────────────────────────────────────
 * `get_contract_insights` answers a contract with no deposit at all with zeros rather than
 * with nothing — `paid_total` and `amount` come back 0 with `count: 0` — and `money(0)` is a
 * perfectly good string. So a caller that guards on the FIGURE prints "Deposit credited
 * GHS 0.00" on a contract that has taken no deposit, which is a row the Schedule's money grid
 * does not carry: its resolver answers NULL there and the row is omitted. Every surface that
 * quotes the grid asks the same question this does — has a deposit payment been RECORDED —
 * and states the figure only then.
 *
 * `paid_total` before `amount` because `amount` is NET of refunds and the grid's row is the
 * gross credited figure; `amount` is the fallback for a payload that carries no gross.
 */
export function scheduleDepositCredited(
    data: ContractInsightsData | null | undefined,
): number | null {
    const deposit = data?.deposit;
    if (!deposit || (deposit.count ?? 0) <= 0) return null;
    const amount = deposit.paid_total ?? deposit.amount;
    return typeof amount === 'number' && Number.isFinite(amount) ? amount : null;
}

/**
 * THE ADVANCE SCHEDULE'S REPAYMENT PAIR — the weekly figure and the Monday it starts on.
 *
 * These are the two rows the Operational Expense Advance's money grid carries, and they are the
 * two the driver is being asked to tick: "Weekly repayment" and "First repayment". A regular
 * advance is repaid weekly from a first Monday until it is cleared — it does not run to a fixed
 * number of instalments, and the row count this list used to state was never a term of the
 * document. It was also the WRONG count on any advance whose weekly figure had been revised,
 * because `loan_instalments` is a RATE HISTORY and not a payment plan: each row says "from this
 * Monday the weekly amount is X", so two rows mean one change of rate, never two payments.
 *
 * ── WHICH ROW IS WHICH, AND WHY NEITHER IS `[0]` ─────────────────────────────
 * The operative weekly amount is the LATEST row's — the rate now in force — and the first
 * repayment date is the EARLIEST row's, the day the schedule began. `get_contract_insights`
 * serves the array `starts_at DESC`, so the two are its first and last elements today; both are
 * taken by extremum instead, for the same reason `scheduleWeeklyInstalment` takes its own — a
 * server-side ORDER BY is not a contract, and an index that silently became the other end of the
 * array would put a superseded figure under a signature.
 *
 * `starts_at` is a `date`, so its ISO strings compare lexicographically in calendar order and no
 * instant is parsed out of a calendar day.
 *
 * NULL UNLESS BOTH RESOLVE. The sentence names an amount and a date together; a half of it is
 * not a shorter term, it is a term that says less than the grid it quotes.
 */
function advanceRepayment(
    instalments: Array<{ amount: number; starts_at: string }> | null | undefined,
): { weekly: number; from: string } | null {
    let latest: { amount: number; starts_at: string } | null = null;
    let earliest: { amount: number; starts_at: string } | null = null;

    for (const row of instalments ?? []) {
        if (!row || typeof row.starts_at !== 'string' || !Number.isFinite(row.amount)) continue;
        if (latest === null || row.starts_at > latest.starts_at) latest = row;
        if (earliest === null || row.starts_at < earliest.starts_at) earliest = row;
    }

    if (latest === null || earliest === null) return null;
    const from = calendarDay(earliest.starts_at);
    return from === null ? null : { weekly: latest.amount, from };
}

function vehicleName(data: ContractInsightsData): string | null {
    const vehicle = data.vehicle;
    if (!vehicle) return null;
    const name = [vehicle.make, vehicle.model].filter(Boolean).join(' ').trim();
    return name.length > 0 ? name : null;
}

// ─── Per doc type ────────────────────────────────────────────────────────────

function hirePurchaseTerms(data: ContractInsightsData): KeyTerm[] {
    const terms: KeyTerm[] = [];
    const name = vehicleName(data);
    const registration = data.vehicle?.registration_number ?? null;
    const price = money(data.contract?.sold_at);

    if (name || registration) {
        const described = [name, registration ? `registration ${registration}` : null]
            .filter(Boolean)
            .join(', ');
        // "ON HIRE PURCHASE", NOT "BUYING". Clause 1.1 of the Schedule lets the Vehicle to
        // the Driver on hire purchase and clause 1.4 keeps it Primelane's property until
        // ownership transfers on completion. A checklist that said "I am buying" would
        // describe a sale the same page says has not happened yet.
        terms.push({
            id: 'vehicle',
            title: 'The vehicle',
            summary: `You are taking the ${name ?? 'vehicle'} on hire purchase`,
            detail: registration ? `Registration ${registration}` : 'Vehicle on this contract',
            confirmation: `I am taking the ${described} on hire purchase from Primelane.`,
        });
    }

    if (price) {
        // THE SCHEDULE'S OWN NAME FOR THIS FIGURE, WHICH IS NO LONGER THE ONE THIS LIST USED.
        // The money grid reads "Hire-purchase price — the total payable to complete the
        // purchase of the Vehicle, made up of the Deposit and the Weekly Instalments", and
        // the no-interest row is "Interest on the Hire-purchase price — None. The
        // Hire-purchase price does not change during the Hire Purchase Period". "Vehicle
        // Purchase Price" was the superseded body's name and the redraft retired it
        // family-wide, so a tick-box carrying it names a figure the document does not.
        terms.push({
            id: 'price',
            title: 'The price',
            summary: `The Hire-purchase price is ${price}`,
            detail: 'Fixed — it does not change while the agreement runs',
            confirmation: `The Hire-purchase price is ${price}, and it does not change.`,
        });
    }

    // THE SCHEDULE'S FIGURE, NOT THE DRIVER'S TOTAL OUTGOING. See
    // `scheduleWeeklyInstalment`: this tick reads "I will pay {weekly} every Monday", the
    // Schedule's money grid prints `{{contract.weekly_instalment}}`, and `weekly_rate` is a
    // larger number on every contract carrying an advance.
    const weeklyAmount = scheduleWeeklyInstalment(data);
    const weekly = money(weeklyAmount);
    if (weekly && (weeklyAmount ?? 0) > 0) {
        // The payment day and the deadline are LITERALS on the Schedule, not merge values —
        // every hire purchase arrangement is Monday by noon — so the checklist can state
        // them, and clause 2.1 is what "until" means. THE GRID ROWS NOW CARRY A NOTICE POWER
        // ("or such other day/time as Primelane notifies the Driver in writing under clause 4.2
        // of the Terms") and 2.1 points at them rather than naming a day. Nothing here changes
        // while Monday by noon is what those rows resolve to on every live contract; the day a
        // contract is served a 4.2 notice, these two strings are what has to follow it.
        terms.push({
            id: 'weekly',
            title: 'My instalment',
            summary: `You pay ${weekly} every Monday`,
            detail: 'By 12:00 noon, until everything owed under this agreement is paid',
            confirmation: `I will pay ${weekly} every Monday by 12:00 noon.`,
        });
    }

    const depositAmount = money(scheduleDepositCredited(data));
    if (depositAmount) {
        const firstPayment = data.deposit?.payments?.[0]?.paid_at ?? null;
        const paidOn = day(firstPayment);
        // "Deposit credited" is the grid's own row for this figure, and clause 2.1 makes it one
        // of the two things the Hire-purchase price is paid by — so the deposit is named
        // against the SAME price the term above it states, under the same name.
        terms.push({
            id: 'deposit',
            title: 'My deposit',
            summary: `Your ${depositAmount} deposit is credited`,
            detail: paidOn ? `Received on ${paidOn}` : 'Credited towards the Hire-purchase price',
            confirmation: paidOn
                ? `My deposit of ${depositAmount}, received on ${paidOn}, is credited towards the Hire-purchase price.`
                : `My deposit of ${depositAmount} is credited towards the Hire-purchase price.`,
        });
    }

    // THE RATE THE DOCUMENT PRINTS, WHICH IS THE DAILY ONE. The Schedule's money grid states
    // the charge as "Calculated on an annual basis and charged daily at
    // {{contract.overdue_daily_rate}} per cent per day on the amount unpaid, for each day it
    // remains unpaid", and clause 2.3 repeats it word for word. It is charged on the AMOUNT
    // UNPAID — not, as this list used to say, on the unpaid instalment only — and the figure is
    // the daily one, the annual basis being how it is worked out rather than what is charged.
    const daily = dailyPercent(data.contract?.overdue_interest_rate);
    if (daily) {
        terms.push({
            id: 'overdue',
            title: 'Late payments',
            summary: `A late payment costs ${daily} per cent a day`,
            detail: 'Charged on the amount unpaid, for each day it stays unpaid',
            confirmation: `I understand a late payment is charged ${daily} per cent a day on the amount unpaid.`,
        });
    }

    return terms;
}

function loanTerms(data: ContractInsightsData, refs: AgreementSubjectRefs): KeyTerm[] {
    const agreement = (data.loan_agreements ?? []).find((row) => row.id === refs.loan_agreement_id);
    if (!agreement) return [];

    const loan = (data.loans ?? []).find((row) => row.id === agreement.loan_id) ?? null;
    const terms: KeyTerm[] = [];

    const amount = money(agreement.loan_amount);
    if (amount) {
        // THE ADVANCE REGISTER, WHICH IS THE PUBLISHED INSTRUMENT'S OWN. This document is
        // titled "Operational Expense Advance"; its Schedule states an "Amount advanced", and
        // clause 1.1 says Primelane HAS ADVANCED it — "by paying it to the Borrower or by paying
        // it to somebody else on the Borrower's behalf". "Borrowing" is the register of a
        // money-lending transaction and appears nowhere in the body the driver has just read.
        // Clause 4.3 of the Advance Terms makes signing an acknowledgement that the Advance HAS
        // BEEN MADE, which is exactly what this tick now says.
        //
        // THE PURPOSE BEFORE THE NARRATION. Two strings have always been called "description"
        // here and only one of them says what the advance is FOR: `loans.description` is the
        // value the Schedule prints as `{{loan.purpose}}` and the admin dossier makes a
        // render-blocking field, while `loan_agreements[].loan_description` is the disbursement
        // TRANSACTION's bank narration, which the driver would otherwise read under a document
        // stating something else. `loan_purpose` is additive and arrives with its own migration,
        // so the narration stays as the fallback rather than being replaced. Both are resolved
        // through `text()` because blank is how the RESOLVER reads a whitespace column, and a
        // purpose the Schedule refuses to print must not win the fallback chain here.
        terms.push({
            id: 'loan-amount',
            title: 'The advance',
            summary: `You were advanced ${amount}`,
            detail:
                text(agreement.loan_purpose) ??
                text(agreement.loan_description) ??
                'Advanced by Primelane under this contract',
            confirmation: `Primelane has advanced me ${amount}.`,
        });
    }

    const rate = percent(loan?.interest_rate);
    if (rate) {
        // FLAT, AND THE SCHEDULE SAYS SO, IN THE WORDS IT NOW USES: "being
        // {{loan.interest_rate}} per cent of the Amount advanced, charged once when the advance
        // is made", which clause 1.3 then spells out — not recalculated on the balance, and it
        // does not grow with time. The confirmation is word for word the Insurance Premium
        // Advance's, because clause 5.1 of the Advance Terms is ONE flat-interest rule over both
        // instruments; the screen is already headed by the document's own published title, so
        // there is nothing left here for a second wording to distinguish.
        terms.push({
            id: 'loan-rate',
            title: 'The interest',
            summary: `Interest is ${rate} of the amount advanced`,
            detail: 'Charged once when the advance is made, and added to what you repay',
            confirmation: `Interest on this advance is ${rate} of the amount advanced, charged once.`,
        });
    }

    // THE SCHEDULE'S TWO REPAYMENT ROWS, STATED AS THE SCHEDULE STATES THEM — see
    // `advanceRepayment`. The advance is repaid weekly from a first Monday until it is cleared;
    // "over N instalments" named a count the document does not carry and read it off a rate
    // history that means something else. Monday is a literal on the grid, as it is on the hire
    // purchase Schedule, so the checklist can say it.
    //
    // GUARDED ON THE FIGURE AND NOT ON ITS FORMATTING, like the deposit and rate rows on the
    // insurance list and the weekly instalment on the hire purchase one. `money(0)` is the
    // perfectly good string "GHS 0.00", so a schedule whose rate in force is zero would collect
    // "I will repay GHS 0.00 every Monday … until this advance is repaid in full" — a term that
    // contradicts itself, under a versioned consent record. Production carries three such rows,
    // each its loan's only one, and `loan_instalments.amount` has no CHECK behind it. A zero
    // weekly is not a shorter term; it is the absence of a rate, and the advance's own card in
    // this app already reads it that way.
    const repayment = advanceRepayment(loan?.instalments);
    const weekly = repayment ? money(repayment.weekly) : null;
    if (repayment && weekly && repayment.weekly > 0) {
        terms.push({
            id: 'loan-instalments',
            title: 'My repayments',
            summary: `Repaid ${weekly} a week`,
            detail: `From ${repayment.from}, every Monday until it is repaid in full`,
            confirmation: `I will repay ${weekly} every Monday from ${repayment.from} until this advance is repaid in full.`,
        });
    }

    const lentOn = day(loan?.lent_at);
    if (lentOn) {
        // `loans.lent_at` KEEPS ITS COLUMN NAME AND LOSES ITS WORDING. What the date records is
        // the day the Advance was made — the Schedules' completion clauses speak of "every advance
        // made to the Borrower" — and a schema name is not a sentence anybody should be asked to
        // tick. Cited by role rather than by number on purpose: the phrase sits at 4.3 of this
        // Schedule and 4.2 of the insurance premium one, and the Advance Terms carry it too at a
        // number that moves between versions.
        terms.push({
            id: 'loan-date',
            title: 'The date',
            summary: `The advance was made on ${lentOn}`,
            detail: 'The date recorded against this contract',
            confirmation: `This advance was made to me on ${lentOn}.`,
        });
    }

    return terms;
}

/**
 * THE TITLE `agreement_render` RETURNS FOR THE RECORD, and the whole of this branch's key.
 *
 * ── WHY THE TITLE AND NOT THE SHAPE OF THE MONEY ─────────────────────────────
 * These sentences are ATTESTATIONS ABOUT THE PAGE THE DRIVER IS SIGNING, not a description of
 * the loan row behind it. `agreement_create` chooses the template ONCE, when the document is
 * raised, and freezes it; the money can be read again at any moment afterwards. Keying the
 * checklist on the money therefore lets the two disagree — and they demonstrably can, in the
 * window where these two apps are live and the migration that teaches `agreement_create` about
 * the Record is not: the server still mints an Insurance Premium Advance on a fully deposited
 * loan, and a money-keyed branch would put "Primelane advanced nothing" over clause 1.1 of an
 * Advance, which says in terms that the Borrower did not pay the whole of it.
 *
 * The title is the document's own answer to which instrument it is. It comes from
 * `agreement_render`, which reads it off the PINNED template row the document was raised
 * against, so it moves only when the document does. Exact match, because a Record is the only
 * thing this list is true of; anything else — including a title this app has never seen — gets
 * the advance checklist, which is what every insurance year in this app has been until now.
 */
const PREMIUM_PAYMENT_RECORD_TITLE = 'Insurance Premium Payment Record';

/**
 * The checklist for a year the DRIVER paid in full — the Insurance Premium Payment Record.
 *
 * ── WHY IT IS A DIFFERENT LIST AND NOT A REWORDED ONE ────────────────────────
 * Every row of the advance checklist states something about an advance: an amount Primelane
 * advanced, a contribution deducted before interest, a rate charged once on that amount, a
 * policy "this advance pays for". On a year the driver paid themselves, all four are false —
 * not awkwardly phrased, false — because Primelane advanced nothing. Clause 17.2 of the
 * Advance Terms says so in terms, and the Record is the document the driver is holding while
 * they tick these boxes [D-B45]. So the branch replaces the list rather than editing it.
 *
 * THE PREMIUM ROW IS THE ONE THAT MATTERS. `insurance_loans.amount` on a Record is the whole
 * premium, so the old row would have read "Primelane has advanced GHS 5,632.00 of your
 * premium" — a driver ticking, under a versioned consent record, a statement that inverts what
 * happened. Nothing about the figure is wrong; the sentence around it is.
 *
 * The three facts the Record's own particulars state — the premium, that the driver paid it in
 * full and when, and that Primelane advanced nothing so nothing is owed — are what the driver
 * confirms, plus the cover the premium buys.
 */
function premiumPaymentTerms(
    loan: NonNullable<ContractInsightsData['insurances'][number]['loan']> | null,
    coverWindow: string | null,
): KeyTerm[] {
    const terms: KeyTerm[] = [];

    // GUARDED ON THE FIGURE AND NOT ONLY ON ITS FORMATTING. `money(0)` is a perfectly good
    // string, and "The premium is GHS 0.00 · you paid it in full" is a pair of sentences no
    // Record carries. The two rows stand or fall together: the second one is about the first.
    const premium = money(loan?.amount);
    if (premium && (loan?.amount ?? 0) > 0) {
        terms.push({
            id: 'insurance-premium',
            title: 'The premium',
            summary: `The premium is ${premium}`,
            detail: 'What this year of cover cost',
            confirmation: `The premium for this year is ${premium}.`,
        });

        // THE DAY IT WAS COMPLETED, which on a premium settled in two transfers is the later
        // of them — see `latestPremiumPaymentAt`. A payment whose date does not resolve still
        // gets its row: the payment is the fact, the date is the detail on it.
        const paidOn = day(latestPremiumPaymentAt(loan));
        terms.push({
            id: 'insurance-premium-paid',
            title: 'My payment',
            summary: paidOn ? `You paid it in full on ${paidOn}` : 'You paid it in full',
            detail: 'Shown in your account as charged and paid',
            confirmation: paidOn
                ? `I paid this premium in full on ${paidOn}.`
                : 'I paid this premium in full.',
        });
    }

    // THE OPERATIVE FACT OF THE WHOLE INSTRUMENT. Clause 17.2: "Where the Borrower pays that
    // premium in full, Primelane advances nothing, no interest arises". It is stated even
    // where no figure above it resolved, because it is true of the year either way — and it is
    // the sentence that distinguishes this document from the one it is not.
    terms.push({
        id: 'insurance-nothing-advanced',
        title: "Primelane's part",
        summary: 'Primelane advanced nothing',
        detail: 'No interest arises, and nothing is owed for this year',
        confirmation:
            'Primelane advanced nothing towards this premium, so no interest arises and ' +
            'nothing is owed for this year.',
    });

    if (coverWindow) {
        // "THIS PREMIUM", NOT "THIS ADVANCE". The advance checklist's cover row confirms that
        // the policy is what the advance pays for; here the premium is what pays for it, and
        // there is no advance to name.
        terms.push({
            id: 'insurance-cover',
            title: 'The cover',
            summary: `The policy runs ${coverWindow}`,
            detail: 'The period this premium buys',
            confirmation: `The policy this premium pays for runs ${coverWindow}.`,
        });
    }

    return terms;
}

function insuranceLoanTerms(data: ContractInsightsData, refs: AgreementSubjectRefs): KeyTerm[] {
    const agreement = (data.insurance_loan_agreements ?? []).find(
        (row) => row.id === refs.insurance_loan_agreement_id,
    );
    if (!agreement) return [];

    const insurance = (data.insurances ?? []).find((row) => row.id === agreement.insurance_id) ?? null;

    const starts = day(insurance?.starts_at);
    const expires = day(insurance?.expires_at);
    const coverWindow = starts || expires ? [starts, expires].filter(Boolean).join(' to ') : null;

    // THE DOCUMENT ON THE SCREEN DECIDES WHICH CHECKLIST THIS IS — see the constant.
    const loan = insurance?.loan ?? null;
    if (refs.title === PREMIUM_PAYMENT_RECORD_TITLE) return premiumPaymentTerms(loan, coverWindow);

    const terms: KeyTerm[] = [];

    const amount = money(insurance?.loan?.amount);
    if (amount) {
        // "ADVANCED", NOT "FINANCING", AND PRIMELANE IS NAMED BECAUSE THE MONEY WENT ELSEWHERE.
        // Clause 1.1: "Primelane has paid the premium … to a licensed insurer. The Borrower did
        // not pay the whole of it; the part Primelane paid is the Amount advanced" — so the
        // actor is Primelane (this is the one advance whose money the driver never touches) and
        // the figure is the grid's own "Amount advanced". "Financing" appears in neither the
        // Schedule nor the Advance Terms, and the present continuous claimed an arrangement
        // still under way when clause 1.1 records a premium already paid.
        //
        // ── "ALONGSIDE YOUR WEEKLY INSTALMENT" IS STATED ONLY WHERE A WEEKLY EXISTS ──
        // A premium advance does not always carry one. Where it does not, the advance is
        // settled at the end of the contract: nothing is repaid alongside the weekly
        // instalment, and Primelane recoups the advance later, out of the contract instalment
        // the driver goes on paying once the vehicle is paid for. A supporting line promising
        // a weekly of the advance's own therefore describes a repayment this arrangement does
        // not have — under a tick-box. So the line is read off the advance's own schedule,
        // through the same `advanceRepayment` the operational advance's repayment term reads
        // its own through, and a schedule that is absent, dateless or zero leaves the row with
        // no supporting line at all.
        //
        // THE ABSENCE IS NEVER TURNED INTO A SENTENCE. Saying "settled at the end" here would
        // be an attestation this app is not in a position to make: both pages are published
        // under the one title "Insurance Premium Advance", `agreement_render` ships the driver
        // no `template_key`, and this file's own rule — WHY THE TITLE AND NOT THE SHAPE OF THE
        // MONEY, on `PREMIUM_PAYMENT_RECORD_TITLE` — is that a checklist keyed on the money can
        // contradict the page it sits over, because the template is frozen when the document is
        // raised and the money is read again at every open. A payload that is missing or stale
        // may therefore only ever take a line away; it may never put one there.
        const repayment = advanceRepayment(insurance?.loan?.instalments);
        terms.push({
            id: 'insurance-amount',
            title: 'The amount advanced',
            summary: `Primelane has advanced ${amount} of your premium`,
            detail:
                repayment && repayment.weekly > 0
                    ? 'Repaid alongside your weekly instalment'
                    : undefined,
            confirmation: `Primelane has advanced ${amount} of my premium.`,
        });
    }

    const deposit = money(insurance?.loan?.deposit_total);
    if (deposit && (insurance?.loan?.deposit_total ?? 0) > 0) {
        terms.push({
            id: 'insurance-deposit',
            title: 'My contribution',
            summary: `You paid ${deposit} up front`,
            detail: 'Deducted before interest is calculated',
            confirmation: `I paid ${deposit} of this premium up front.`,
        });
    }

    // GUARDED ON THE RATE AND NOT ON ITS FORMATTING, like the deposit row above it and the
    // weekly instalment on the hire purchase list. `percent(0)` is the string "0%", so a loan
    // carrying an explicit zero rate produced "Interest is 0%, flat" — a term the Schedule's
    // grid does not print and a tick that confirms a charge nobody makes.
    const rateValue = insurance?.loan?.interest_rate;
    const rate = percent(rateValue);
    if (rate && (rateValue ?? 0) > 0) {
        // NOT "on the financed balance" — the Schedule's grid reads "{{insurance_loan.
        // interest_rate}} per cent, flat — worked out once on the Amount advanced", and the
        // same grid says repaying early does not reduce the Total repayable. A balance the
        // interest tracked would make that second sentence untrue. THE CONFIRMATION IS SHARED
        // WITH `loan-rate` WORD FOR WORD and the two move together: clause 5.1 of the Advance
        // Terms is one flat-interest rule over both instruments, and "this insurance loan" named
        // an instrument the registry no longer publishes under that name.
        terms.push({
            id: 'insurance-rate',
            title: 'The interest',
            summary: `Interest is ${rate}, flat`,
            detail: 'Worked out once on the amount advanced, not on the balance left',
            confirmation: `Interest on this advance is ${rate} of the amount advanced, charged once.`,
        });
    }

    if (coverWindow) {
        terms.push({
            id: 'insurance-cover',
            title: 'The cover',
            summary: `The policy runs ${coverWindow}`,
            detail: 'The period this premium buys',
            confirmation: `The policy this advance pays for runs ${coverWindow}.`,
        });
    }

    return terms;
}

function depositTerms(data: ContractInsightsData, refs: AgreementSubjectRefs): KeyTerm[] {
    const agreement = (data.deposit_agreements ?? []).find(
        (row) => row.id === refs.contract_deposit_agreement_id,
    );
    if (!agreement) return [];

    const terms: KeyTerm[] = [];
    const amount = money(agreement.deposit_amount);
    const paidOn = day(agreement.deposit_date);

    if (amount) {
        terms.push({
            id: 'deposit-amount',
            title: 'The deposit',
            summary: `Primelane has received ${amount} from you`,
            detail: paidOn ? `Paid on ${paidOn}` : 'Credited against this contract',
            confirmation: `I paid a deposit of ${amount}${paidOn ? ` on ${paidOn}` : ''}.`,
        });
    }

    // THE SECOND TERM USED TO NAME THE VEHICLE, AND THE BODY NOW CONTRADICTS THAT. The
    // deposit agreement opens by saying it "is made before any vehicle is allocated, and
    // allocates none", and clause 2.2 says paying does not allocate a particular vehicle or
    // oblige Primelane to allocate one. "This deposit is held against the Toyota Corolla,
    // registration GT 1234-25" is a sentence the document denies — on the very document the
    // driver is being asked to tick it against. What clause 2.1 does promise is the limb
    // stated here, and it needs no vehicle row to be true, so it is no longer conditional on
    // one resolving.
    terms.push({
        id: 'deposit-purpose',
        title: 'What it is for',
        summary: 'It counts towards a vehicle on hire purchase',
        detail: 'It does not reserve a particular vehicle',
        confirmation:
            'I understand this deposit counts towards what I pay for a vehicle, and does ' +
            'not reserve one.',
    });

    return terms;
}

// ─── Entry point ─────────────────────────────────────────────────────────────

function fallbackTerm(refs: AgreementSubjectRefs): KeyTerm {
    return {
        id: 'read',
        title: 'The document',
        summary: 'Read the whole document before you sign',
        detail: refs.reference_no ? `Reference ${refs.reference_no}` : 'Every page matters',
        confirmation: 'I have read the whole of this document and the details in it are correct.',
    };
}

export function agreementKeyTerms(
    refs: AgreementSubjectRefs,
    data: ContractInsightsData | null | undefined,
): KeyTerm[] {
    if (!data) return [fallbackTerm(refs)];

    let terms: KeyTerm[] = [];
    switch (refs.doc_type) {
        case 'HIRE_PURCHASE':
            terms = hirePurchaseTerms(data);
            break;
        case 'LOAN':
            terms = loanTerms(data, refs);
            break;
        case 'INSURANCE_LOAN':
            terms = insuranceLoanTerms(data, refs);
            break;
        case 'DEPOSIT':
            terms = depositTerms(data, refs);
            break;
        default:
            terms = [];
    }

    return terms.length > 0 ? terms : [fallbackTerm(refs)];
}
